import { Button } from "@/components/core/buttons/button";
import invoiceService from "@/features/dashboard/api/services/invoice-service";
import * as Dialog from "@radix-ui/react-dialog";
import { IconAlertTriangle, IconX } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";

interface CancelInvoiceModalProps {
	isOpen: boolean;
	onClose: () => void;
	invoiceId: string;
	invoiceNumber: string;
	hasCollection: boolean;
}

export function CancelInvoiceModal({ isOpen, onClose, invoiceId, invoiceNumber, hasCollection }: CancelInvoiceModalProps) {
	const queryClient = useQueryClient();
	const [reason, setReason] = useState("");

	useEffect(() => {
		if (!isOpen) setReason("");
	}, [isOpen]);

	const cancelMutation = useMutation({
		mutationFn: () => invoiceService.cancel(invoiceId, reason.trim()),
		onSuccess: (result) => {
			const msg = result.books?.reclassify_id ? "Invoice cancelled. Customer balance moved to Customer Deposits (2050)." : "Invoice cancelled.";
			toast.success(msg);
			queryClient.invalidateQueries({ queryKey: ["invoice"] });
			queryClient.invalidateQueries({ queryKey: ["invoices"] });
			queryClient.invalidateQueries({ queryKey: ["invoice-request"] });
			queryClient.invalidateQueries({ queryKey: ["invoice-requests"] });
			onClose();
		},
		onError: (err: { response?: { data?: { message?: string } } }) => {
			const msg = err.response?.data?.message ?? "Failed to cancel invoice";
			toast.error(msg);
		},
	});

	const trimmed = reason.trim();
	const isValid = trimmed.length >= 3;
	const isPending = cancelMutation.isPending;

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!isValid || isPending) return;
		cancelMutation.mutate();
	};

	return (
		<Dialog.Root open={isOpen} onOpenChange={(open) => !open && !isPending && onClose()}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
				<Dialog.Content className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-xl max-h-[90vh] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border bg-white p-6 shadow-lg overflow-y-auto">
					<div className="flex items-center justify-between">
						<Dialog.Title className="text-xl font-semibold text-red-700">Cancel Invoice {invoiceNumber}</Dialog.Title>
						<Dialog.Close asChild>
							<Button variant="ghost" size="sm" className="h-8 w-8 p-0" disabled={isPending}>
								<IconX className="h-4 w-4" />
							</Button>
						</Dialog.Close>
					</div>

					<Dialog.Description className="text-sm text-gray-600">
						This will reverse the journal entry in Books (audit trail preserved). The original invoice stays in the books for BIR audit but is marked as
						cancelled.
					</Dialog.Description>

					{hasCollection && (
						<div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm">
							<div className="flex gap-2">
								<IconAlertTriangle className="h-4 w-4 mt-0.5 text-amber-700 shrink-0" />
								<div>
									<div className="font-semibold text-amber-900">This invoice has been paid</div>
									<p className="mt-1 text-xs text-amber-800">
										Cancelling will leave a negative AR balance, which Books will automatically reclassify to <strong>Customer Deposits (2050)</strong>. The
										customer's payment stays in your bank but you now formally owe them that amount as credit. Issue a refund separately if needed.
									</p>
								</div>
							</div>
						</div>
					)}

					<form onSubmit={handleSubmit} className="space-y-4">
						<div>
							<label htmlFor="cancel-reason" className="block text-sm font-medium text-gray-700">
								Reason for cancellation <span className="text-red-600">*</span>
							</label>
							<textarea
								id="cancel-reason"
								value={reason}
								onChange={(e) => setReason(e.target.value)}
								rows={4}
								maxLength={1000}
								placeholder="e.g., Wrong customer, duplicate billing, customer cancelled order, BIR re-issuance needed"
								required
								autoFocus
								className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
							/>
							{reason.length > 0 && !isValid && <p className="mt-1 text-xs text-amber-700">At least 3 characters required.</p>}
							<p className="mt-1 text-xs text-gray-500">Required for BIR audit trail.</p>
						</div>

						<div className="flex justify-end gap-2 border-t pt-4">
							<Button type="button" variant="ghost" onClick={onClose} disabled={isPending}>
								Keep invoice
							</Button>
							<Button type="submit" variant="danger" disabled={!isValid || isPending}>
								{isPending ? "Cancelling..." : "Cancel invoice"}
							</Button>
						</div>
					</form>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
